import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, X, Edit, Search, Phone, Mail, MapPin } from 'lucide-react';
import { calcularDigitoVerificador, documentoCompleto, TIPOS_DOCUMENTO } from '../../utils/ruc';
import { mensajeDeError } from '../../utils/errores';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario/clientes/`;

export default function ClientesManager() {
    const [clientes, setClientes] = useState([]);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const FORM_VACIO = {
        id: null, nombre: '', tipo_documento: 'ci', numero_documento: '', digito_verificador: '',
        telefono: '', email: '', direccion: '', activo: true,
    };
    const [formData, setFormData] = useState(FORM_VACIO);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchClientes = async (q = '') => {
        try {
            const url = q ? `${API}?q=${encodeURIComponent(q)}` : API;
            const res = await axios.get(url);
            setClientes(res.data);
        } catch { setError('Error al cargar clientes.'); }
    };

    useEffect(() => { fetchClientes(); }, []);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        fetchClientes(e.target.value);
    };

    const handleOpenModal = (cliente = null) => {
        setFormData(cliente
            ? {
                id: cliente.id,
                nombre: cliente.nombre,
                tipo_documento: cliente.tipo_documento || 'ci',
                numero_documento: cliente.numero_documento || '',
                digito_verificador: cliente.digito_verificador || '',
                telefono: cliente.telefono || '',
                email: cliente.email || '',
                direccion: cliente.direccion || '',
                activo: cliente.activo,
            }
            : FORM_VACIO
        );
        setError(null);
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // El dígito sólo tiene sentido en un RUC: si el tipo es otro, se
            // manda vacío en vez de arrastrar el que quedó de antes.
            const data = {
                ...formData,
                digito_verificador: formData.tipo_documento === 'ruc' ? formData.digito_verificador : '',
            };
            if (formData.id) {
                await axios.put(`${API}${formData.id}/`, data);
            } else {
                await axios.post(API, data);
            }
            setIsModalOpen(false);
            fetchClientes(search);
        } catch (err) {
            setError(mensajeDeError(err, 'Error al guardar el cliente.'));
        } finally { setLoading(false); }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl">
                        <Users size={24} className="text-brand-800" />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Clientes</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">{clientes.length} clientes registrados</p>
                    </div>
                </div>
                <div className="flex gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search size={16} className="icono-campo" />
                        <input
                            type="text" placeholder="Buscar por nombre o RUC..."
                            value={search} onChange={handleSearch}
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all whitespace-nowrap shadow-lg shadow-brand-500/30"
                    >
                        <Plus size={18} /> Nuevo Cliente
                    </button>
                </div>
            </div>

            {error && !isModalOpen && (
                <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-4 rounded-xl">{error}</div>
            )}

            {/* Grid de clientes */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {clientes.map(c => (
                    <div key={c.id} className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl p-5 hover:border-brand-500/40 transition-all group">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-8 h-8 rounded-full bg-brand-500/20 flex items-center justify-center text-brand-800 font-bold text-sm">
                                        {c.nombre.charAt(0).toUpperCase()}
                                    </div>
                                    <h3 className="font-bold text-slate-900 dark:text-white">{c.nombre}</h3>
                                </div>
                                {c.documento_completo && (
                                    <p className="text-xs text-slate-400 dark:text-gray-500 font-mono ml-10">
                                        {c.tipo_documento === 'ruc' ? 'RUC' : 'CI'}: {c.documento_completo}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => handleOpenModal(c)}
                                className="p-1.5 rounded-lg bg-slate-100 dark:bg-white/10 hover:bg-brand-500/30 text-slate-500 dark:text-gray-400 hover:text-brand-800 transition-colors"
                            >
                                <Edit size={14} />
                            </button>
                        </div>
                        <div className="space-y-2 text-sm">
                            {c.telefono && (
                                <div className="flex items-center gap-2 text-slate-500 dark:text-gray-400">
                                    <Phone size={13} className="text-slate-400 dark:text-gray-600" />
                                    {c.telefono}
                                </div>
                            )}
                            {c.email && (
                                <div className="flex items-center gap-2 text-slate-500 dark:text-gray-400">
                                    <Mail size={13} className="text-slate-400 dark:text-gray-600" />
                                    {c.email}
                                </div>
                            )}
                            {c.direccion && (
                                <div className="flex items-start gap-2 text-slate-500 dark:text-gray-400">
                                    <MapPin size={13} className="text-slate-400 dark:text-gray-600 mt-0.5 shrink-0" />
                                    <span className="line-clamp-2">{c.direccion}</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-white/5 flex justify-between items-center">
                            <span className="text-xs text-slate-400 dark:text-gray-600">
                                Desde {new Date(c.created_at).toLocaleDateString()}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.activo ? 'bg-exito-500/20 text-exito-400' : 'bg-error-500/20 text-error-400'}`}>
                                {c.activo ? 'Activo' : 'Inactivo'}
                            </span>
                        </div>
                    </div>
                ))}
                {clientes.length === 0 && (
                    <div className="col-span-3 text-center py-16 text-slate-400 dark:text-gray-500">
                        <Users size={48} className="mx-auto mb-4 opacity-30" />
                        <p>No hay clientes registrados. Agrega el primero.</p>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fondo-modal">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-white/10">
                            <h3 className="titulo-modal">
                                <Users className="text-brand-800" size={20} />
                                {formData.id ? 'Editar Cliente' : 'Nuevo Cliente'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="boton-icono"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {error && <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-3 rounded-xl text-sm">{error}</div>}

                            <div>
                                <label htmlFor="cliente-nombre-completo" className="etiqueta-campo normal-case text-sm">Nombre Completo *</label>
                                <input id="cliente-nombre-completo" required type="text" className="campo" placeholder="Juan Pérez"
                                    value={formData.nombre} onChange={e => {
                                        const val = e.target.value;
                                        const cursor = e.target.selectionStart;
                                        // En mayúsculas, como va en un comprobante. Antes se ponía
                                        // mayúscula inicial por palabra, y eso rompe lo que no es un
                                        // nombre común: «ECA S.A» quedaba «Eca S.a». Además el usuario
                                        // escribía en mayúsculas y el sistema se lo cambiaba sin decir
                                        // por qué, que es de las cosas que más desconciertan.
                                        setFormData({ ...formData, nombre: val.toUpperCase() });
                                        requestAnimationFrame(() => e.target.setSelectionRange(cursor, cursor));
                                    }} />
                            </div>
                            {/* El tipo va separado del número porque la factura
                                electrónica tiene que declarar cuál de los dos es. */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="cliente-tipo-documento" className="etiqueta-campo normal-case text-sm">Tipo de documento</label>
                                    <select id="cliente-tipo-documento" className="campo"
                                        value={formData.tipo_documento}
                                        onChange={e => {
                                            const tipo = e.target.value;
                                            setFormData({
                                                ...formData,
                                                tipo_documento: tipo,
                                                digito_verificador: tipo === 'ruc'
                                                    ? calcularDigitoVerificador(formData.numero_documento)
                                                    : '',
                                            });
                                        }}>
                                        {TIPOS_DOCUMENTO.map(t => (
                                            <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="cliente-telefono" className="etiqueta-campo normal-case text-sm">Teléfono</label>
                                    <input id="cliente-telefono" type="tel" className="campo" placeholder="0981 123 456"
                                        value={formData.telefono} onChange={e => setFormData({ ...formData, telefono: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="col-span-2">
                                    <label htmlFor="cliente-numero-documento" className="etiqueta-campo normal-case text-sm">Número</label>
                                    <input id="cliente-numero-documento" type="text" className="campo font-mono" placeholder="80012345"
                                        value={formData.numero_documento}
                                        onChange={e => {
                                            const numero = e.target.value;
                                            // El dígito se completa solo: es un dato
                                            // derivado, no algo que el usuario tenga
                                            // que calcular en el mostrador.
                                            setFormData({
                                                ...formData,
                                                numero_documento: numero,
                                                digito_verificador: formData.tipo_documento === 'ruc'
                                                    ? calcularDigitoVerificador(numero)
                                                    : '',
                                            });
                                        }} />
                                </div>
                                <div>
                                    <label htmlFor="cliente-digito" className="etiqueta-campo normal-case text-sm">Dígito</label>
                                    <input id="cliente-digito" type="text" maxLength={1}
                                        className="campo font-mono text-center disabled:opacity-40"
                                        disabled={formData.tipo_documento !== 'ruc'}
                                        value={formData.digito_verificador}
                                        onChange={e => setFormData({ ...formData, digito_verificador: e.target.value.replace(/\D/g, '') })} />
                                </div>
                            </div>
                            {formData.tipo_documento === 'ruc' && formData.numero_documento && (
                                <p className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                    Documento: {documentoCompleto(formData)}
                                </p>
                            )}
                            <div>
                                <label htmlFor="cliente-email" className="etiqueta-campo normal-case text-sm">Email</label>
                                <input id="cliente-email" type="email" className="campo" placeholder="juan@ejemplo.com"
                                    value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                            </div>
                            <div>
                                <label htmlFor="cliente-direccion" className="etiqueta-campo normal-case text-sm">Dirección</label>
                                <textarea id="cliente-direccion" rows={2} className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-brand-500" placeholder="Av. Mariscal López 1234"
                                    value={formData.direccion} onChange={e => setFormData({ ...formData, direccion: e.target.value })} />
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-white/5 rounded-xl">
                                <label htmlFor="cliente-activo" className="text-sm font-semibold text-slate-600 dark:text-gray-300 flex-1">Cliente Activo</label>
                                <button type="button" id="cliente-activo" role="switch" aria-checked={formData.activo} onClick={() => setFormData({ ...formData, activo: !formData.activo })}
                                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${formData.activo ? 'bg-brand-500' : 'bg-gray-600'}`}>
                                    <span className={`inline-block w-5 h-5 bg-white rounded-full transition-transform ${formData.activo ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                            <div className="pie-modal">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">Cancelar</button>
                                <button type="submit" disabled={loading} className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 transition-all">
                                    {loading ? 'Guardando...' : (formData.id ? 'Guardar Cambios' : 'Crear Cliente')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
